/** Тонкий клиент к API. Токен в localStorage, ошибки — исключениями. */

import type {
  FilterState,
  Me,
  PanelResponse,
  ParseResponse,
  PersonSuggestion,
  ProgramSuggestion,
  Reference,
  ReferencePerson,
  ReferenceProgram,
  ReextractResult,
  ReplyResponse,
} from "./types";

const BASE = "/api/v1";
const TOKEN_KEY = "mediaai.token";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Token ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  const body = await response.text();
  const parsed: unknown = body ? JSON.parse(body) : null;

  // 403 по доходу — это осмысленный ответ карточки, а не сбой: заглушка
  // «нет доступа» полезнее пустого места, поэтому тело отдаётся наверх.
  if (response.status === 403 && isForbidden(parsed)) return parsed as T;

  if (!response.ok) {
    if (response.status === 401) setToken(null);
    throw new ApiError(response.status, detailOf(parsed) ?? `ошибка ${response.status}`);
  }
  return parsed as T;
}

function isForbidden(value: unknown): boolean {
  return typeof value === "object" && value !== null && (value as { status?: string }).status === "forbidden";
}

function detailOf(value: unknown): string | null {
  if (typeof value === "object" && value !== null) {
    const detail = (value as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
    const nonField = (value as { non_field_errors?: unknown }).non_field_errors;
    if (Array.isArray(nonField) && typeof nonField[0] === "string") return nonField[0];
  }
  return null;
}

export async function login(email: string, password: string): Promise<string> {
  const data = await request<{ token: string }>("/auth/token/", {
    method: "POST",
    body: JSON.stringify({ username: email, password }),
  });
  setToken(data.token);
  return data.token;
}

export const fetchMe = (): Promise<Me> => request<Me>("/me/");

export const fetchPanel = (tool: string, state: FilterState, signal?: AbortSignal) =>
  request<PanelResponse>(`/panels/${tool}/`, {
    method: "POST",
    body: JSON.stringify({ state }),
    signal,
  });

export const parseMessage = (message: string, state: FilterState, tool: string) =>
  request<ParseResponse>("/chat/parse/", {
    method: "POST",
    body: JSON.stringify({ message, state, tool }),
  });

export const requestReply = (message: string, state: FilterState, tool: string) =>
  request<ReplyResponse>("/chat/reply/", {
    method: "POST",
    body: JSON.stringify({ message, state, tool }),
  });

export const suggestPeople = (q: string) =>
  request<{ results: PersonSuggestion[] }>(`/people/?q=${encodeURIComponent(q)}`);

export const suggestPrograms = (q: string) =>
  request<{ results: ProgramSuggestion[] }>(`/programs/?q=${encodeURIComponent(q)}`);

// --------------------------------------------------------------- справочники
// Все вызовы закрыты на сервере IsAdminUser. Кнопка на экране прячется от
// не-администратора для порядка, но защита — там, а не здесь.

export const fetchReference = () => request<Reference>("/reference/");

export const patchProgram = (
  id: number,
  body: { is_hidden?: boolean; permanent_host_id?: number | null; co_host_ids?: number[] },
) =>
  request<{ program: ReferenceProgram; needs_reextract: boolean }>(`/reference/programs/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const reextractProgram = (id: number) =>
  request<ReextractResult>(`/reference/programs/${id}/reextract/`, { method: "POST" });

export const patchPerson = (id: number, body: { is_hidden?: boolean }) =>
  request<{ person: ReferencePerson }>(`/reference/people/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const mergePerson = (id: number, targetId: number) =>
  request<{ moved: number; dropped: number; person: ReferencePerson }>(
    `/reference/people/${id}/merge/`,
    { method: "POST", body: JSON.stringify({ target_id: targetId }) },
  );

export const setPersonRole = (id: number, programId: number, role: string) =>
  request<{ changed: number; dropped: number; role: string; person: ReferencePerson }>(
    `/reference/people/${id}/role/`,
    { method: "POST", body: JSON.stringify({ program_id: programId, role }) },
  );
